import Sanscript from '@indic-transliteration/sanscript';

export interface TransliterationScheme {
  /** Sanscript roman scheme name to transliterate FROM (what the user types). */
  from: string;
  /** Sanscript Brahmic/script scheme name to transliterate TO. */
  to: string;
  /** Display name for the toggle UI, e.g. "Tamil". */
  name: string;
  options?: { syncope?: boolean };
}

// Dravidian languages use `itrans_dravidian` (distinguishes the short/long
// "e"/"o" these languages have but Indo-Aryan ones don't) and skip
// `syncope` — schwa deletion is a Hindi-family phenomenon, not a Dravidian
// one, so applying it there would drop vowels that are actually pronounced.
// Indo-Aryan languages use plain `itrans` with `syncope: true` so e.g.
// "ajay" comes out "अजय" instead of the literal (and wrong-sounding)
// "अजय्".
const LANGUAGE_SCHEME: Record<string, TransliterationScheme> = {
  ta: { from: 'itrans_dravidian', to: 'tamil', name: 'Tamil' },
  te: { from: 'itrans_dravidian', to: 'telugu', name: 'Telugu' },
  kn: { from: 'itrans_dravidian', to: 'kannada', name: 'Kannada' },
  ml: { from: 'itrans_dravidian', to: 'malayalam', name: 'Malayalam' },
  hi: { from: 'itrans', to: 'devanagari', name: 'Hindi', options: { syncope: true } },
  mr: { from: 'itrans', to: 'devanagari', name: 'Marathi', options: { syncope: true } },
  ne: { from: 'itrans', to: 'devanagari', name: 'Nepali', options: { syncope: true } },
  bn: { from: 'itrans', to: 'bengali', name: 'Bengali', options: { syncope: true } },
  as: { from: 'itrans', to: 'assamese', name: 'Assamese', options: { syncope: true } },
  gu: { from: 'itrans', to: 'gujarati', name: 'Gujarati', options: { syncope: true } },
  pa: { from: 'itrans', to: 'gurmukhi', name: 'Punjabi', options: { syncope: true } },
  or: { from: 'itrans', to: 'oriya', name: 'Odia', options: { syncope: true } },
  si: { from: 'itrans', to: 'sinhala', name: 'Sinhala', options: { syncope: true } },
  // Urdu deliberately omitted: Sanscript's `urdu` scheme is tuned for fully-
  // vocalized academic transliteration and marks every short vowel with an
  // explicit diacritic (zabar/zer/pesh) — correct phonetically, but it reads
  // as heavily over-diacritized, unnatural Urdu (ordinary Urdu text omits
  // most short-vowel marks). No combination of options fixed this in
  // testing, so it's left out rather than shipping a result that looks
  // wrong to a native reader.
};

// Dravidian languages distinguish several consonants (retroflex vs dental
// "n"/"l"/"t"/"d", the "zh" sound) that casual English spelling doesn't
// mark at all — ITRANS resolves the ambiguity via capitalization, which a
// phonetic-English typist won't know to use unless told. Indo-Aryan
// languages don't need this note: their common-word spellings map onto
// ITRANS far more directly.
export const DRAVIDIAN_TRANSLITERATION_HINT =
  'Tip: capital N/L/T/D picks the retroflex sound (e.g. vaNakkam → வணக்கம்); lowercase picks the dental/alveolar one.';

const DRAVIDIAN_LANGUAGES = new Set(['ta', 'te', 'kn', 'ml']);

export function isDravidianTransliteration(languageCode?: string): boolean {
  return !!languageCode && DRAVIDIAN_LANGUAGES.has(languageCode);
}

/** Null for languages with no transliteration engine here — Latin languages, Arabic, Russian, CJK. Those either don't need script conversion or need a fundamentally different IME technology this library doesn't provide. */
export function transliterationSchemeForLanguage(languageCode?: string): TransliterationScheme | null {
  if (!languageCode) return null;
  return LANGUAGE_SCHEME[languageCode] || null;
}

const WORD_CHAR = /[A-Za-z0-9']/;
const BOUNDARY_CHAR = /[\s.,!?;:)\]}"]/;

/** All-caps tokens (AI, BBC, NASA) are far more likely to be intentional acronyms the user wants to keep in Latin than words they're phonetically spelling out — skip those rather than mangling them into a phonetic native-script guess. */
function looksLikeAcronym(word: string): boolean {
  return word.length >= 2 && word === word.toUpperCase() && /[A-Z]/.test(word);
}

export function transliterateWord(word: string, scheme: TransliterationScheme): string {
  if (!word || looksLikeAcronym(word)) return word;
  return Sanscript.t(word, scheme.from, scheme.to, scheme.options);
}

/**
 * Word-boundary live transliteration: when the user's last keystroke was a
 * single boundary character (space/punctuation) completing a run of Latin
 * letters, converts just that word to the target script in place and
 * returns the adjusted value + cursor position. Returns null for anything
 * else (paste, mid-text edits, deletions, selections) so those are always
 * left completely untouched — only a just-finished word of plain typing
 * ever gets rewritten, never existing content.
 */
export function applyWordBoundaryTransliteration(
  oldValue: string,
  newValue: string,
  newCursor: number,
  scheme: TransliterationScheme
): { value: string; cursor: number } | null {
  if (newValue.length !== oldValue.length + 1) return null;
  if (newCursor < 1 || newCursor > newValue.length) return null;

  const insertedChar = newValue[newCursor - 1];
  if (insertedChar === undefined || !BOUNDARY_CHAR.test(insertedChar)) return null;

  let start = newCursor - 1;
  while (start > 0 && WORD_CHAR.test(newValue[start - 1]!)) start--;
  const word = newValue.slice(start, newCursor - 1);
  if (!word) return null;

  const converted = transliterateWord(word, scheme);
  if (converted === word) return null;

  const value = newValue.slice(0, start) + converted + newValue.slice(newCursor - 1);
  const cursor = start + converted.length + 1; // +1 for the boundary char itself
  return { value, cursor };
}
